import { useThree } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import { debounceTime, fromEvent, tap } from 'rxjs';

import { TOOL_CONST } from '@/constant';
import { useSubscriptions } from '@/package';
import { useToolMainCameraStore } from '@/store';

const useSaveAndRestoreCamera = () => {
  const { modeIndex, getMode, getSavedCameraInfo, saveCameraInfo } =
    useToolMainCameraStore();
  const mode = useMemo(getMode, [modeIndex]);
  const { camera, controls } = useThree();
  const isTopMode = useMemo(
    () => mode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP,
    [mode],
  );

  useSubscriptions(() => {
    if (!controls || (isTopMode && camera.isPerspectiveCamera)) {
      return [];
    }

    return [
      fromEvent(controls, 'change')
        .pipe(
          debounceTime(30),
          tap(() => {
            saveCameraInfo(camera, controls, mode);
          }),
        )
        .subscribe(),
    ];
  }, [controls, mode]);

  useEffect(() => {
    const savedCameraInfo = getSavedCameraInfo(mode);
    if (!controls || !savedCameraInfo) return;

    camera.position.set(...savedCameraInfo.position);
    camera.quaternion.set(...savedCameraInfo.quaternion);
    controls.target.set(...savedCameraInfo.target);

    camera.updateProjectionMatrix();
    controls.update();
  }, [camera, controls, mode]);
};

export default useSaveAndRestoreCamera;
